import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { QuickAddBar } from '@/components/tasks/QuickAddBar';
import { UpcomingItem } from '@/components/tasks/UpcomingItem';
import { UpcomingProjectCard } from '@/components/tasks/UpcomingProjectCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const err = (e, d) => toast.error(e?.response?.data?.detail || d);
const rank = (p) => (p.stalled ? 0 : p.next_up.some(t => t.carried_over) || p.todos.some(t => t.carried_over) ? 1 : p.next_up.length || p.todos.length ? 2 : p.total === 0 ? 3 : 4);

const Stat = ({ label, value, tone, testId }) => (
  <div className="bg-white border border-stone-200 rounded-lg px-4 py-3">
    <p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p>
    <p className={`text-2xl font-semibold tabular-nums mt-1 ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

export default function UpcomingTasks() {
  const [data, setData] = useState(null);
  const [stallDays, setStallDays] = useState(() => localStorage.getItem('stall_days') || '3');

  const load = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/tasks/upcoming?stall_days=${stallDays}`, cfg)
      .then(r => setData(r.data)).catch(e => err(e, 'Failed to load upcoming tasks'));
  }, [stallDays]);

  useEffect(() => { localStorage.setItem('stall_days', stallDays); load(); }, [load, stallDays]);

  const call = async (fn, okMsg, undo) => {
    try {
      await fn(); load();
      if (okMsg) toast.success(okMsg, undo ? { action: { label: 'Undo', onClick: async () => { await undo(); load(); } } } : undefined);
      return true;
    } catch (e) { err(e, 'Action failed'); return false; }
  };

  const toggleTask = (t) => call(() => axios.post(`${BACKEND_URL}/api/tasks/${t.task_id}/toggle`, {}, cfg), `Done: ${t.title}`,
    () => axios.post(`${BACKEND_URL}/api/tasks/${t.task_id}/toggle`, {}, cfg).catch(e => err(e, 'Undo failed')));
  const toggleTodo = (t) => call(() => axios.post(`${BACKEND_URL}/api/todos/${t.todo_id}/toggle`, {}, cfg), `Done: ${t.text}`,
    () => axios.post(`${BACKEND_URL}/api/todos/${t.todo_id}/toggle`, {}, cfg).catch(e => err(e, 'Undo failed')));
  const deleteTodo = (t) => window.confirm('Delete this quick job?') && call(() => axios.delete(`${BACKEND_URL}/api/todos/${t.todo_id}`, cfg), 'Quick job deleted');
  const addTodo = (text, project_id) => call(() => axios.post(`${BACKEND_URL}/api/todos`, { text, project_id }, cfg), 'Quick job added');
  const loadChecklist = (p) => call(() => axios.post(`${BACKEND_URL}/api/projects/${p.project_id}/tasks/apply-template`, {}, cfg), 'Checklist loaded');

  if (!data) return <div className="text-stone-600">Loading upcoming tasks...</div>;
  const projects = [...data.projects].sort((a, b) => rank(a) - rank(b));
  const all = [...data.general_todos, ...projects.flatMap(p => [...p.next_up, ...p.todos])];

  return (
    <div className="space-y-6 pt-10 lg:pt-0" data-testid="upcoming-tasks-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Upcoming Tasks</h1>
          <p className="mt-2 text-base text-stone-600">The next 3 tasks on every active project, in checklist order.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-stone-600">
          Flag stalled after
          <Select value={stallDays} onValueChange={setStallDays}>
            <SelectTrigger className="h-8 w-24 text-xs border-stone-200" data-testid="stall-days-select"><SelectValue /></SelectTrigger>
            <SelectContent>{['2', '3', '5', '7', '14'].map(d => <SelectItem key={d} value={d}>{d} days</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Active projects" value={projects.length} tone="text-stone-900" testId="stat-projects" />
        <Stat label="Up next" value={all.length} tone="text-stone-900" testId="stat-upnext" />
        <Stat label="Carried over" value={all.filter(i => i.carried_over).length} tone="text-amber-700" testId="stat-carried" />
        <Stat label="Stalled projects" value={projects.filter(p => p.stalled).length} tone="text-red-700" testId="stat-stalled" />
      </div>

      <QuickAddBar projects={projects} onAdd={addTodo} />

      {data.general_todos.length > 0 && (
        <section className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="upcoming-general">
          <header className="px-4 py-3 border-b border-stone-100 text-sm font-semibold text-stone-900">General quick jobs</header>
          <div className="divide-y divide-stone-100">
            {data.general_todos.map(t => <UpcomingItem key={t.todo_id} item={t} kind="todo" onToggle={toggleTodo} onDelete={deleteTodo} />)}
          </div>
        </section>
      )}

      {projects.length === 0 && <p className="text-sm text-stone-500" data-testid="upcoming-empty">No active projects.</p>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {projects.map(p => (
          <UpcomingProjectCard key={p.project_id} p={p} stallDays={data.stall_days}
            onToggleTask={toggleTask} onToggleTodo={toggleTodo} onDeleteTodo={deleteTodo} onLoadChecklist={loadChecklist} />
        ))}
      </div>
    </div>
  );
}
